import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogService } from '../../../../core/services/dialog.service';
import { PARENT_DIALOG_PANEL_CLASS } from '../../../../shared/constants/parent-dialog.constants';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { PendingKidComponent } from '../../../user/components/pending-kid/pending-kid.component';
import { UserService } from '../../../user/service/user.service';
import {
  FamilyKidsService,
  type FamilyChild,
} from '../../../parent-family/services/family-kids.service';
import { ParentIdentitySectionComponent } from '../../components/parent-identity-section/parent-identity-section.component';
import { IdentityNavService } from '../../services/identity-nav.service';
import { ParentDisclosureService } from '../../services/parent-disclosure.service';
import { ParentIdentityService } from '../../services/parent-identity.service';

const VERIFIED_PREVIEW_LIMIT = 4;

@Component({
  selector: 'app-parent-identity-hub',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ParentButtonComponent,
    ParentIdentitySectionComponent,
  ],
  templateUrl: './parent-identity-hub.component.html',
  styleUrl: './parent-identity-hub.component.scss',
})
export class ParentIdentityHubComponent implements OnInit {
  #router = inject(Router);
  #dialog = inject(DialogService);
  #userService = inject(UserService);
  #familyKids = inject(FamilyKidsService);
  #identity = inject(ParentIdentityService);
  #disclosure = inject(ParentDisclosureService);
  #nav = inject(IdentityNavService);

  readonly verifiedPreviewLimit = VERIFIED_PREVIEW_LIMIT;
  readonly showAllVerified = signal(false);

  readonly user = computed(() => this.#userService.user());
  readonly children = this.#familyKids.children;
  readonly unverifiedKids = this.#nav.unverifiedKids;
  readonly verifiedKids = computed(() =>
    this.children().filter((child) => !!child.verified),
  );
  readonly visibleVerifiedKids = computed(() => {
    const verified = this.verifiedKids();
    if (this.showAllVerified() || verified.length <= VERIFIED_PREVIEW_LIMIT) {
      return verified;
    }
    return verified.slice(0, VERIFIED_PREVIEW_LIMIT);
  });
  readonly pendingDisclosureCount = computed(
    () => this.#disclosure.pendingCount(),
  );
  readonly activeGrantCount = computed(
    () => this.#disclosure.activeGrantCount(),
  );
  readonly hasAttention = this.#nav.hasAttention;

  async ngOnInit(): Promise<void> {
    if (!this.#identity.layerEnabled()) {
      await this.#router.navigate(['/home']);
      return;
    }

    await this.#nav.whenReady();
  }

  kidName(child: FamilyChild): string {
    return this.#nav.kidDisplayName(child);
  }

  kidInitial(child: FamilyChild): string {
    const name = this.kidName(child).trim();
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  toggleVerifiedKids(): void {
    this.showAllVerified.update((open) => !open);
  }

  openKidVerification(child: FamilyChild): void {
    void this.#dialog.open(
      PendingKidComponent,
      { user: child },
      {
        backdropClass: 'dialog-backdrop',
        panelClass: PARENT_DIALOG_PANEL_CLASS,
      },
    );
  }

  openDisclosureApprovals(): void {
    void this.#router.navigate(['/identity/pending-approvals']);
  }

  openDisclosureGrants(): void {
    void this.#router.navigate(['/identity/connected-apps']);
  }
}
